import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  BookOpen, Send, ArrowLeft, RefreshCw, Download,
  Filter, Shield, AlertTriangle, FolderKanban, Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import DiaryEntryCard from "../components/DiaryEntryCard";
import VoiceRecorder from "../components/VoiceRecorder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ENTRY_TYPES = [
  { value: "observation", label: "Observation" },
  { value: "decision", label: "Decision" },
  { value: "issue", label: "Issue" },
  { value: "assumption", label: "Assumption" },
  { value: "activity", label: "Activity" },
];

export default function DiaryPage({ projectId: propProjectId, embedded }) {
  const params = useParams();
  const navigate = useNavigate();
  const effectiveProjectId = propProjectId || params.projectId;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(effectiveProjectId || "");
  const [entries, setEntries] = useState([]);
  const [input, setInput] = useState("");
  const [entryType, setEntryType] = useState("observation");
  const [submitting, setSubmitting] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterSafety, setFilterSafety] = useState(false);

  useEffect(() => {
    if (!embedded) {
      axios.get(`${BACKEND_URL}/api/projects`).then((res) => setProjects(res.data)).catch(() => {});
    }
  }, [embedded]);

  useEffect(() => {
    if (selectedProject) {
      loadEntries();
    } else {
      setEntries([]);
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (effectiveProjectId) setSelectedProject(effectiveProjectId);
  }, [effectiveProjectId]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${selectedProject}/diary`);
      setEntries(res.data);
    } catch {
      toast.error("Failed to load diary entries");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const text = input.trim();
    if (!text || !selectedProject) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${selectedProject}/diary`, {
        content: text,
        entry_type: entryType,
      });
      setEntries((prev) => [res.data, ...prev]);
      setInput("");
      toast.success("Diary entry recorded and structured by AI");
    } catch {
      toast.error("Failed to create diary entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoiceTranscription = (text, source) => {
    setInput((prev) => (prev ? prev + " " + text : text));
    toast.success(`Voice transcribed (${source === "whisper" ? "Whisper" : "Browser"})`);
  };

  const handleEntryUpdate = (updated) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const handleEntryDelete = async (entryId) => {
    if (!window.confirm("Delete this diary entry?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${selectedProject}/diary/${entryId}`);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      toast.success("Entry deleted");
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  const handleSynthesize = async () => {
    setSynthesizing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${selectedProject}/diary/synthesize`);
      toast.success("Diary synthesized for report integration");
      // Could show result in a modal, for now just notify
    } catch (err) {
      toast.error(err.response?.data?.detail || "Synthesis failed");
    } finally {
      setSynthesizing(false);
    }
  };

  const filteredEntries = filterSafety
    ? entries.filter((e) => e.safety_relevant)
    : entries;

  const safetyCount = entries.filter((e) => e.safety_relevant).length;
  const pendingFollowups = entries.filter((e) => e.ai_followup && !e.ai_followup_answered).length;

  return (
    <div className={`space-y-4 ${embedded ? "" : ""}`} data-testid="diary-page">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#003057]" data-testid="diary-title">
              Engineering Diary
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              AI-assisted test diary for audit-ready documentation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-64 rounded-sm text-xs" data-testid="diary-project-select">
                <FolderKanban className="w-3.5 h-3.5 mr-1 text-slate-400" />
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {!selectedProject && !embedded ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
          <CardContent className="p-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
            <p className="text-sm text-slate-500">Select a project to start your engineering diary</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats bar */}
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>{entries.length} entries</span>
            {safetyCount > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <Shield className="w-3 h-3" /> {safetyCount} safety-relevant
              </span>
            )}
            {pendingFollowups > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="w-3 h-3" /> {pendingFollowups} follow-ups pending
              </span>
            )}
            <div className="flex-1" />
            <Button
              variant={filterSafety ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterSafety(!filterSafety)}
              className={`rounded-sm text-xs h-7 ${filterSafety ? "bg-red-600 hover:bg-red-700 text-white" : "border-slate-300"}`}
              data-testid="filter-safety-btn"
            >
              <Shield className="w-3 h-3 mr-1" /> Safety Only
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSynthesize}
              disabled={synthesizing || entries.length === 0}
              className="rounded-sm text-xs h-7 border-[#003057] text-[#003057]"
              data-testid="synthesize-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${synthesizing ? "animate-spin" : ""}`} />
              Synthesize for Report
            </Button>
          </div>

          {/* Input area */}
          <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="diary-input-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Select value={entryType} onValueChange={setEntryType}>
                    <SelectTrigger className="w-36 rounded-sm text-xs h-8" data-testid="entry-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTRY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <VoiceRecorder onTranscription={handleVoiceTranscription} disabled={submitting} />
                </div>
              </div>
              <Textarea
                data-testid="diary-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder='Dictate or type your diary entry... e.g., "Today we ran HIL regression on SW version 3.2.1, observed intermittent CAN timeout during cold start..."'
                className="rounded-sm resize-none min-h-[80px]"
                rows={3}
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-400">
                  AI will structure your entry and ask follow-up questions for completeness
                </p>
                <Button
                  data-testid="submit-diary-btn"
                  onClick={handleSubmit}
                  disabled={submitting || !input.trim()}
                  className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1" /> Record Entry
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Entries list */}
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-slate-200 rounded-sm" />
              ))}
            </div>
          ) : filteredEntries.length === 0 ? (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">
                  {filterSafety
                    ? "No safety-relevant entries"
                    : "No diary entries yet. Start documenting your test activities."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="diary-entries-list">
              {filteredEntries.map((entry) => (
                <DiaryEntryCard
                  key={entry.id}
                  entry={entry}
                  projectId={selectedProject}
                  onUpdate={handleEntryUpdate}
                  onDelete={handleEntryDelete}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
